import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { ErreurApi } from '../../api/client.js';
import { Lien } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS, styleBouton } from '../../app/styles.js';

/**
 * Carte « Comptes rendus à faire » du Pilotage (PARITE-4, démo isabelle/ComptesRendus, réunion du 16/10 #31) : lit
 * `comptesRendus.aTraiter` (tous dossiers) et mène à l'onglet « Comptes rendus » du dossier, où l'on génère, relit et valide.
 */
export interface LigneCompteRendu {
  id: string;
  dossierId: string;
  dossierReference: string;
  dossierTitle: string;
  kind: 'entretien' | 'appel';
  title: string;
  status: 'a_faire' | 'a_relire' | 'valide';
  createdAt: string;
  generatedAt: string | null;
  validatedAt: string | null;
  validatedByDisplayName: string | null;
}

export interface ComptesRendusATraiter {
  aFaire: readonly LigneCompteRendu[];
  aRelire: readonly LigneCompteRendu[];
  valides: readonly LigneCompteRendu[];
}

const NATURES = { entretien: 'Entretien', appel: 'Appel' } as const;
const dateFr = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'America/Guadeloupe', day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13 };
const ligne: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '8px 0',
  borderTop: '1px solid #EEEDF3',
};
const lien: CSSProperties = {
  ...styleBouton,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: CIBLE_MIN,
  textDecoration: 'none',
  boxSizing: 'border-box',
};
const versDossier = (l: LigneCompteRendu): string => `/dossier?dossier=${encodeURIComponent(l.dossierId)}&vue=comptes-rendus`;

function Groupe({
  titre,
  lignes,
  detail,
  action,
}: {
  titre: string;
  lignes: readonly LigneCompteRendu[];
  detail: (l: LigneCompteRendu) => string;
  action: string;
}) {
  if (lignes.length === 0) return null;
  return (
    <>
      <h3 style={{ margin: '6px 0 0', fontSize: 15, color: COULEURS.marine }}>{titre}</h3>
      <ul aria-label={titre} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {lignes.map((l) => (
          <li key={l.id} style={ligne}>
            <span style={{ flex: '1 1 220px', minWidth: 0 }}>
              <strong>{l.title}</strong>
              <br />
              <span style={discret}>{`${l.dossierTitle} · ${detail(l)}`}</span>
            </span>
            <Lien vers={versDossier(l)} enfants={action} style={lien} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function CarteComptesRendus({ lire }: { lire: () => Promise<ComptesRendusATraiter> }) {
  const [donnees, setDonnees] = useState<ComptesRendusATraiter | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setDonnees(await lire());
    } catch (e) {
      setErreur(messageDe(e));
    }
  }, [lire]);
  useEffect(() => {
    void charger();
  }, [charger]);

  return (
    <section aria-label="Comptes rendus à faire" data-composant="comptes-rendus" style={carte}>
      <h2
        style={{ margin: 0, fontSize: 18, color: COULEURS.marine }}
      >{`Comptes rendus à faire${donnees ? ` (${donnees.aFaire.length})` : ''}`}</h2>
      <p style={{ margin: 0, ...discret }}>Interne au cabinet : jamais transmis aux héritiers ni au notaire.</p>
      {erreur ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
            {erreur}
          </p>
          <button type="button" style={styleBouton} onClick={() => void charger()}>
            Réessayer
          </button>
        </div>
      ) : null}
      {!donnees && !erreur ? <p role="status">Chargement des comptes rendus…</p> : null}
      {donnees && donnees.aFaire.length === 0 ? (
        <p style={{ margin: 0 }}>Aucun compte rendu à faire. Un entretien ou un appel ajouté dans un dossier apparaît ici.</p>
      ) : null}
      {donnees ? (
        <>
          <Groupe titre="À faire" lignes={donnees.aFaire} detail={(l) => `${NATURES[l.kind]} du ${dateFr(l.createdAt)}`} action="Ouvrir" />
          <Groupe
            titre="À relire"
            lignes={donnees.aRelire}
            detail={(l) => `généré le ${dateFr(l.generatedAt)}, à vérifier`}
            action="Relire"
          />
          <Groupe
            titre="Derniers validés"
            lignes={donnees.valides}
            detail={(l) => `validé le ${dateFr(l.validatedAt)} par ${l.validatedByDisplayName ?? ''}`}
            action="Lire"
          />
        </>
      ) : null}
    </section>
  );
}
